import React from 'react';
import classNames from 'classnames';
import useSheet from 'app/jss';
import { noop, get } from 'lodash';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import Icon from 'app/components/commons/Icon';
import Tappable from 'app/components/commons/Tappable';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import AnimatedGroupChild from 'app/components/commons/AnimatedGroupChild';
import { Circ } from 'app/constants/cssEase';


type Props = {
  classes: object,
  children?: any,
  currentIndex: number,
  onIndexChange?: (index: number) => void,
  onEnter?: () => void,
  onLeave?: () => void,
  hideArrows?: boolean,
};

type State = {};

class CarouselRotating extends React.Component {
  props: Props;
  state: State = {};

  static defaultProps = {
    children: undefined,
    onIndexChange: noop,
    onEnter: undefined,
    onLeave: undefined,
    hideArrows: false,
  };

  nextPage = () => {
    const {
      currentIndex,
      children,
      onIndexChange,
    } = this.props;
    if (currentIndex !== (React.Children.count(children) - 1)) {
      onIndexChange(currentIndex + 1);
    }
  }

  previousPage = () => {
    if (this.props.currentIndex !== 0) {
      this.props.onIndexChange(this.props.currentIndex - 1);
    }
  }

  processChildren = (children) => {
    const {
      classes,
      currentIndex,
    } = this.props;

    return children.map((child, index) => {
      const positionDiff = currentIndex - index;
      const isCurrent = index === currentIndex;
      const style = {};
      switch (positionDiff) {
        case -3:
          style.opacity = 0;
          style.transform = 'translateX(0) scale(0) translateZ(0px)';
          style.zIndex = 0;
          break;
        case -2:
          style.opacity = 0.4;
          style.transform = 'translateX(20%) scale(0.8) translateZ(0px)';
          style.zIndex = 1;
          break;
        case -1:
          style.opacity = 1;
          style.transform = 'translateX(10%) scale(0.9) translateZ(1px)';
          style.zIndex = 2;
          break;
        case 0:
          style.opacity = 1;
          style.transform = 'translateX(0) scale(1) translateZ(3px)';
          style.zIndex = 3;
          break;
        case 1:
          style.opacity = 1;
          style.transform = 'translateX(-10%) scale(0.9) translateZ(2px)';
          style.zIndex = 2;
          break;
        case 2:
          style.opacity = 0.4;
          style.transform = 'translateX(-20%) scale(0.8) translateZ(1px)';
          style.zIndex = 1;
          break;
        case 3:
          style.opacity = 0;
          style.transform = 'translateX(0) scale(0) translateZ(0px)';
          style.zIndex = 0;
          break;
        default:
          // don't render items outside 3 to the left/right
          // even though the 3rd one is at opacity 0, we need it
          // to exist so we can transition it from 0 to 0.4 when it moves closer
          return undefined;
      }

      return (
        <div
          className={classNames(classes.carouselItem, {
            current: isCurrent,
          })}
          style={style}
          key={child.key}
        >
          {child}
        </div>
      );
    });
  }

  render() {
    const {
      classes,
      children,
      currentIndex,
      onEnter,
      onLeave,
      hideArrows,
    } = this.props;

    const maxIndex = React.Children.count(children) - 1;
    const groupElements = this.processChildren(React.Children.toArray(children));

    return (
      <AnimatedGroup
        className={classes.carouselContainer}
        onEnter={Enter.appear()}
        onLeave={Leave.disappear()}
      >
        {!hideArrows &&
          <div className={classNames(classes.arrowContainer, 'left')} key="left">
            <Tappable
              component="div"
              className={classNames(classes.arrow, { disabled: currentIndex === 0 })}
              onTap={this.previousPage}
              disabled={currentIndex === 0}
            >
              <div className={classes.arrowInner} />
              <Icon className={classes.arrowIcon} type="icon-left-arrow" />
            </Tappable>
          </div>
        }
        <AnimatedGroup onEnter={onEnter} onLeave={onLeave}>
          {groupElements}
        </AnimatedGroup>
        {!hideArrows &&
          <div className={classNames(classes.arrowContainer, 'right')} key="right">
            <Tappable
              component="div"
              className={classNames(classes.arrow, { disabled: currentIndex === maxIndex })}
              onTap={this.nextPage}
              disabled={currentIndex === maxIndex}
            >
              <div className={classes.arrowInner} />
              <Icon className={classes.arrowIcon} type="icon-right-arrow" />
            </Tappable>
          </div>
        }
      </AnimatedGroup>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    arrowContainer: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      top: 0,
      bottom: 0,
      '&.left': {
        left: 0,
      },
      '&.right': {
        right: 0,
      },
    },
    arrow: {
      height: 50,
      width: 50,
      borderRadius: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'border-color 0.2s, opacity 0.2s',
      transform: 'translateZ(0)',
      border: {
        width: 3,
        style: 'solid',
        color: theme.color.basis4,
      },
      position: 'relative',
      '&.disabled': {
        opacity: 0.5,
        cursor: 'not-allowed !important',
      },
      '&:hover:not(.disabled) $arrowInner': {
        transform: 'scale(1)',
        transition: `transform 0.2s ${Circ.easeOut}`,
      },
    },
    arrowInner: {
      borderRadius: '100%',
      transform: 'scale(0)',
      transition: `transform 0.2s ${Circ.easeIn}`,
      backgroundColor: theme.color.basis4,
      position: 'absolute',
      top: -1,
      left: -1,
      right: -1,
      bottom: -1,
      opacity: 0.5,
    },
    arrowIcon: {
      zIndex: 1,
    },
    carouselContainer: {
      width: '100%',
      height: '100%',
    },
    carouselItem: {
      transition: 'opacity 0.5s, transform 0.5s, z-index 0.5s',
      position: 'absolute',
      borderRadius: 8,
      overflow: 'hidden',
      height: '100%',
      // we want 70% width and centered
      left: '15%',
      right: '15%',
    },
  };
}

export default useSheet(applyStyles)(CarouselRotating);
